// The page-context hint the web publishes with a Copilot turn (SCRUM-306).
//
// A hint, not an authorization: the runtime resolves every ref against the
// caller's own permissions, so the worst a wrong row here can do is produce a
// refusal. What the table must never do is leak an identifier into `route` —
// the concrete path `/patients/f47ac10b` would put a patient id in every log
// line that records the route. `route` is always the PATTERN; ids live in
// `refs`, which the runtime treats as untrusted.

import type {
  PageContextRef,
  PageContextRefType,
  PageContextRequestV1,
} from "@/generated/copilot-contracts";

type PageContextRoute = {
  /** The SPA route pattern, exactly as `src/routes/routes.tsx` declares it. */
  pattern: string;
  /** The ref type this screen is about. */
  type: PageContextRefType;
};

/**
 * The routes that publish a hint in v1: the patient pages, and nothing else.
 * `/patients/:id/record` comes first because it is the longer of the two
 * patterns and the match is first-wins.
 *
 * List pages such as `/crm/leads`, `/schedulings` and `/inventory` are
 * deliberately absent. They carry no id, so the only thing they could publish
 * is the route itself — and a route-only context renders no block in the
 * runtime prompt and narrows no tool set, so publishing it would describe the
 * turn as a page-context turn that never happened.
 * `/financial/account/:id/entries` is absent for a different reason — it is a
 * `finance_account`, which is not one of the four supported types.
 */
export const PAGE_CONTEXT_ROUTES: readonly PageContextRoute[] = [
  { pattern: "/patients/:id/record", type: "patient" },
  { pattern: "/patients/:id", type: "patient" },
];

function segmentsOf(path: string): string[] {
  return path.split("/").filter((segment) => segment.length > 0);
}

/** The id the pattern's `:id` captured, `null` when the pattern has none, and
 * `undefined` when the path does not match the pattern at all. */
function matchRoute(pattern: string, pathname: string): string | null | undefined {
  const patternSegments = segmentsOf(pattern);
  const pathSegments = segmentsOf(pathname);
  if (patternSegments.length !== pathSegments.length) return undefined;

  let id: string | null = null;
  for (let index = 0; index < patternSegments.length; index += 1) {
    const expected = patternSegments[index];
    const actual = pathSegments[index];
    if (expected === ":id") {
      if (actual.length === 0) return undefined;
      id = actual;
      continue;
    }
    if (expected !== actual) return undefined;
  }
  return id;
}

/** The hint for a pathname, or `null` when this screen publishes nothing. The
 * transport then omits the key entirely. */
export function pageContextFor(pathname: string): PageContextRequestV1 | null {
  for (const route of PAGE_CONTEXT_ROUTES) {
    const id = matchRoute(route.pattern, pathname);
    if (id === undefined) continue;

    const refs: PageContextRef[] = id === null ? [] : [{ id, type: route.type }];
    return {
      version: "copilot/page-context-request/v1",
      route: route.pattern,
      refs,
    };
  }
  return null;
}
